import type { ReactNode } from "react";
import { Link } from "wouter";
import { QrCode } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";

/**
 * Shared glassmorphism shell for the Login and Signup pages.
 *
 * Theme-aware: light values are the default, dark values apply when an
 * ancestor carries the `dark` class (or data-theme="dark"). All colours are
 * CSS variables scoped to `.cqr-auth`, so the look does not depend on the
 * Tailwind theme tokens.
 */

export const glassInput = "cqr-input";
export const glassInputGroup = "cqr-group";
export const glassInputBare = "cqr-bare";
export const glassLabelBase = "cqr-label block text-sm font-medium";
export const glassLabel = `${glassLabelBase} mb-2`;
export const glassError = "cqr-err text-sm mt-1.5";
export const glassLink = "cqr-link";
export const glassMuted = "cqr-muted";
export const glassFaint = "cqr-faint";
export const glassCta = "cqr-cta";

const AUTH_CSS = `
.cqr-auth {
  --cqr-text: #0f172a;
  --cqr-muted: rgba(15, 23, 42, 0.66);
  --cqr-faint: rgba(15, 23, 42, 0.5);
  --cqr-placeholder: rgba(15, 23, 42, 0.38);
  --cqr-card: rgba(255, 255, 255, 0.55);
  --cqr-card-border: rgba(15, 23, 42, 0.1);
  --cqr-card-hi: rgba(255, 255, 255, 0.95);
  --cqr-card-shadow: 0 30px 70px -30px rgba(15, 23, 42, 0.28), 0 8px 24px -12px rgba(15, 23, 42, 0.1);
  --cqr-sheen-a: rgba(255, 255, 255, 0.9);
  --cqr-sheen-b: rgba(15, 23, 42, 0.03);
  --cqr-field: rgba(15, 23, 42, 0.03);
  --cqr-field-hover: rgba(15, 23, 42, 0.05);
  --cqr-field-focus: rgba(255, 255, 255, 0.9);
  --cqr-field-border: rgba(15, 23, 42, 0.14);
  --cqr-field-border-hover: rgba(15, 23, 42, 0.28);
  --cqr-focus: #6366f1;
  --cqr-focus-ring: rgba(99, 102, 241, 0.2);
  --cqr-focus-glow: rgba(99, 102, 241, 0.45);
  --cqr-link: #4338ca;
  --cqr-link-hover: #312e81;
  --cqr-ok: #047857;
  --cqr-warn: #b45309;
  --cqr-err: #dc2626;
  --cqr-line: rgba(15, 23, 42, 0.2);
  color: var(--cqr-text);
}
.dark .cqr-auth,
[data-theme="dark"] .cqr-auth {
  --cqr-text: #ffffff;
  --cqr-muted: rgba(255, 255, 255, 0.68);
  --cqr-faint: rgba(255, 255, 255, 0.5);
  --cqr-placeholder: rgba(255, 255, 255, 0.36);
  --cqr-card: rgba(255, 255, 255, 0.05);
  --cqr-card-border: rgba(255, 255, 255, 0.14);
  --cqr-card-hi: rgba(255, 255, 255, 0.28);
  --cqr-card-shadow: 0 30px 80px -30px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.02);
  --cqr-sheen-a: rgba(255, 255, 255, 0.09);
  --cqr-sheen-b: rgba(255, 255, 255, 0.04);
  --cqr-field: rgba(255, 255, 255, 0.07);
  --cqr-field-hover: rgba(255, 255, 255, 0.1);
  --cqr-field-focus: rgba(255, 255, 255, 0.12);
  --cqr-field-border: rgba(255, 255, 255, 0.16);
  --cqr-field-border-hover: rgba(255, 255, 255, 0.3);
  --cqr-focus: #818cf8;
  --cqr-focus-ring: rgba(129, 140, 248, 0.25);
  --cqr-focus-glow: rgba(129, 140, 248, 0.6);
  --cqr-link: #a5b4fc;
  --cqr-link-hover: #c7d2fe;
  --cqr-ok: #34d399;
  --cqr-warn: #fbbf24;
  --cqr-err: #f87171;
  --cqr-line: rgba(255, 255, 255, 0.22);
}


.cqr-card {
  position: relative;
  border-radius: 2rem;
  background: var(--cqr-card);
  border: 1px solid var(--cqr-card-border);
  -webkit-backdrop-filter: blur(22px) saturate(180%);
  backdrop-filter: blur(22px) saturate(180%);
  box-shadow: var(--cqr-card-shadow), inset 0 1px 0 var(--cqr-card-hi), inset 0 -1px 0 var(--cqr-sheen-b);
}
.cqr-card::before {
  content: ""; position: absolute; inset: 0; border-radius: inherit; pointer-events: none;
  background: linear-gradient(135deg, var(--cqr-sheen-a) 0%, transparent 32%, transparent 68%, var(--cqr-sheen-b) 100%);
}

.cqr-label { color: var(--cqr-text); opacity: 0.88; }
.cqr-muted { color: var(--cqr-muted); }
.cqr-faint { color: var(--cqr-faint); }
.cqr-ok { color: var(--cqr-ok); }
.cqr-warn { color: var(--cqr-warn); }
.cqr-err { color: var(--cqr-err); }
.cqr-link { color: var(--cqr-link); transition: color 0.2s; }
.cqr-link:hover { color: var(--cqr-link-hover); text-decoration: underline; }
.cqr-line-l { flex: 1; height: 1px; background: linear-gradient(to right, transparent, var(--cqr-line)); }
.cqr-line-r { flex: 1; height: 1px; background: linear-gradient(to left, transparent, var(--cqr-line)); }

.cqr-input, .cqr-group, .cqr-google {
  width: 100%; height: 3rem; border-radius: 0.75rem;
  background: var(--cqr-field);
  border: 1px solid var(--cqr-field-border);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: inset 0 1px 0 var(--cqr-sheen-a);
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s, transform 0.2s;
}
.cqr-input { padding: 0 1rem; color: var(--cqr-text); font-size: 1rem; outline: none; }
.cqr-group { display: flex; align-items: center; padding: 0 2.5rem 0 1rem; position: relative; font-size: 1rem; }
.cqr-bare { flex: 1; min-width: 0; height: 100%; background: transparent; color: var(--cqr-text); font-size: inherit; outline: none; border: 0; }
.cqr-input::placeholder, .cqr-bare::placeholder { color: var(--cqr-placeholder); }
@media (min-width: 768px) { .cqr-input, .cqr-group { font-size: 0.875rem; } }
.cqr-input:hover, .cqr-group:hover { background: var(--cqr-field-hover); border-color: var(--cqr-field-border-hover); }
.cqr-input:focus, .cqr-group:focus-within {
  background: var(--cqr-field-focus);
  border-color: var(--cqr-focus);
  box-shadow: inset 0 1px 0 var(--cqr-sheen-a), 0 0 0 4px var(--cqr-focus-ring), 0 0 26px -4px var(--cqr-focus-glow);
}
.cqr-group[data-state="ok"]   { --cqr-focus: var(--cqr-ok);   --cqr-focus-ring: rgba(16, 185, 129, 0.22); --cqr-focus-glow: rgba(16, 185, 129, 0.5); border-color: var(--cqr-ok); }
.cqr-group[data-state="warn"] { --cqr-focus: var(--cqr-warn); --cqr-focus-ring: rgba(245, 158, 11, 0.22); --cqr-focus-glow: rgba(245, 158, 11, 0.5); border-color: var(--cqr-warn); }
.cqr-group[data-state="err"]  { --cqr-focus: var(--cqr-err);  --cqr-focus-ring: rgba(239, 68, 68, 0.22);  --cqr-focus-glow: rgba(239, 68, 68, 0.5);  border-color: var(--cqr-err); }

.cqr-google {
  display: flex; align-items: center; justify-content: center; gap: 0.75rem;
  color: var(--cqr-text); font-size: 0.875rem; font-weight: 600; cursor: pointer;
}
.cqr-google:hover { background: var(--cqr-field-hover); border-color: var(--cqr-field-border-hover); transform: translateY(-2px); }
.cqr-google:active { transform: translateY(0); }
.cqr-google:focus-visible { outline: none; box-shadow: 0 0 0 4px var(--cqr-focus-ring); }

.cqr-cta {
  width: 100%; height: 3rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  border-radius: 0.75rem; border: 1px solid rgba(255, 255, 255, 0.25);
  color: #ffffff; font-size: 0.875rem; font-weight: 600; cursor: pointer;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%);
  box-shadow: 0 0 34px -6px rgba(139, 92, 246, 0.85), inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
}
.cqr-cta:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); box-shadow: 0 0 46px -4px rgba(139, 92, 246, 1), inset 0 1px 0 rgba(255, 255, 255, 0.35); }
.cqr-cta:active:not(:disabled) { transform: translateY(0); filter: brightness(0.95); }
.cqr-cta:focus-visible { outline: none; box-shadow: 0 0 0 4px rgba(167, 139, 250, 0.45), 0 0 34px -6px rgba(139, 92, 246, 0.85); }
.cqr-cta:disabled { opacity: 0.6; cursor: not-allowed; }

.cqr-logo {
  display: inline-flex; padding: 0.75rem; border-radius: 1rem; color: #ffffff;
  background: linear-gradient(135deg, #6366f1 0%, #9333ea 100%);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 0 30px -6px rgba(129, 140, 248, 0.95), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  transition: transform 0.2s, box-shadow 0.2s;
}
.cqr-logo:hover { transform: scale(1.05); box-shadow: 0 0 40px -4px rgba(129, 140, 248, 1), inset 0 1px 0 rgba(255, 255, 255, 0.45); }

.cqr-auth input:-webkit-autofill,
.cqr-auth input:-webkit-autofill:hover,
.cqr-auth input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--cqr-text);
  caret-color: var(--cqr-text);
  transition: background-color 99999s ease-out 0s;
}

@keyframes cqr-card-in { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
.cqr-card-in { animation: cqr-card-in 0.5s ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .cqr-card-in { animation: none; }
}
`;

export function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

interface AuthShellProps {
  title: string;
  subtitle: string;
  dividerText: string;
  onGoogle: () => void;
  children: ReactNode;
  footer: ReactNode;
}

export function AuthShell({ title, subtitle, dividerText, onGoogle, children, footer }: AuthShellProps) {
  return (
    <>
      <Navbar />
      <style>{AUTH_CSS}</style>
      <div className="cqr-auth bg-background relative min-h-[calc(100vh-57px)] flex items-center justify-center px-4 py-12">
        {/* Transparent glass card */}
        <div className="cqr-card cqr-card-in w-full max-w-md p-8">
          <div className="relative">
            <div className="flex justify-center mb-6">
              <Link href="/" aria-label="CommonQR home" className="cqr-logo">
                <QrCode size={32} strokeWidth={2.5} />
              </Link>
            </div>

            <h1 className="text-2xl font-semibold text-center tracking-tight mb-2">{title}</h1>
            <p className="cqr-muted text-center text-sm mb-8">{subtitle}</p>

            <button type="button" onClick={onGoogle} className="cqr-google mb-6">
              <GoogleIcon />
              Continue with Google
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="cqr-line-l" />
              <span className="cqr-faint text-xs font-medium">{dividerText}</span>
              <div className="cqr-line-r" />
            </div>

            {children}

            <p className="cqr-muted text-center text-sm mt-8">{footer}</p>
          </div>
        </div>
      </div>
    </>
  );
}
